import { useMemo, useState } from 'react'
import { formatDate, ownerName, makeComparator, sportMeta } from '../../../lib/championships.js'
import {
  PARTNERSHIP_STAGES, PARTNERSHIP_DEFINITION, partnershipMeta, partnershipRollup,
} from '../cheer.js'
import { incumbent, rollup as incumbentRollup, HERFF_JONES, PACKAGE_PRICE } from '../../../lib/cheerIncumbentData.js'
import NextStep from './NextStep.jsx'
import SubmitLink from './SubmitLink.jsx'
import styles from './cheer.module.css'
import shared from './pursuit.module.css'

/**
 * THE GYM PARTNERSHIP PIPELINE — the Cheer space's own view, and the reason
 * cheer is its own animal rather than another line on a shared board.
 *
 * It answers three questions at once, in the order a rep actually asks them:
 *
 *   1. WHERE IS THE TAM? Four pipeline columns — Target · In pursuit · Contract
 *      signed · Partner — with live counts. Every cheer gym is in exactly one,
 *      so the columns always sum to the filtered universe.
 *   2. WHICH GYMS MATTER MOST? Each card carries the gym's CHAMPIONSHIP
 *      FOOTPRINT — how many titles its teams have won, across how many teams —
 *      and the board sorts by it by default. A gym winning a stack of titles
 *      without us is the strongest possible target.
 *   3. WHAT IS THE MOVE? Every card ends in its ONE numbered next step. Nobody
 *      has to work out what to do with a gym from its columns.
 *
 * The team layer is expandable per card: a gym's teams and each team's titles,
 * every one of them clickable through to the championship record. That is the
 * gym → team → championship hierarchy, rendered.
 */

const SORTS = [
  { key: 'titles', label: 'Championship footprint', dir: 'desc' },
  { key: 'name', label: 'Gym name', dir: 'asc' },
  { key: 'state', label: 'State', dir: 'asc' },
  { key: 'owner', label: 'Assigned rep', dir: 'asc' },
  { key: 'partnership', label: 'Pipeline stage', dir: 'asc' },
]

/** Show this many gyms per column before asking — a page, never a cap. */
const PAGE = 40

export default function CheerGyms({
  items, now, onOpen, onOpenTitle, rowAction = null, rollup = null, noun = 'Gym',
}) {
  const [sort, setSort] = useState('titles')
  // The championship-footprint SEGMENT — Brian's "sort by established
  // championship relationships", as a filter as well as a sort.
  const [footprint, setFootprint] = useState('all')

  const active = SORTS.find((s) => s.key === sort) || SORTS[0]
  const cmp = useMemo(() => makeComparator(active.key, active.dir, now), [active, now])

  const scoped = useMemo(() => {
    if (footprint === 'titled') return items.filter((c) => (c.titleCount || 0) > 0)
    if (footprint === 'none') return items.filter((c) => !(c.titleCount || 0))
    return items
  }, [items, footprint])

  const stages = useMemo(() => partnershipRollup(scoped), [scoped])
  const byStage = useMemo(() => {
    const out = Object.fromEntries(PARTNERSHIP_STAGES.map((s) => [s.key, []]))
    for (const c of scoped) out[partnershipMeta(c).key].push(c)
    for (const k of Object.keys(out)) out[k].sort(cmp)
    return out
  }, [scoped, cmp])

  const titleTotal = useMemo(() => scoped.reduce((n, c) => n + (c.titleCount || 0), 0), [scoped])
  const partners = stages.find((s) => s.key === 'partner')?.count || 0

  /* ⚠️ WHO ALREADY SUPPLIES THESE GYMS — and how much of the competitor's book
     we hold no record for at all. Herff Jones publishes its entire cheer client
     list; matched against ours it splits the work in two, and the second half
     is the one that never appears on this board today because those gyms are
     not in HubSpot yet. A TAM that only counts records we already have cannot
     show its own gap. Read-only overlay — nothing is written to any record. */
  const hj = useMemo(() => incumbentRollup(scoped), [scoped])

  return (
    <div className={styles.wrap}>
      {/* WHAT A PARTNERSHIP IS — stated on the surface, because half of it
          (the exclusive branded jewelry program) is invisible in a deal stage. */}
      <div className={styles.definition}>
        <strong>Partnership = both halves.</strong> {PARTNERSHIP_DEFINITION}
      </div>

      {/* THE TAM SCOREBOARD. Coverage, not vanity: how much of the universe is
          actually partnered, and how big the championship footprint under it is. */}
      <div className={styles.scoreRow}>
        <Score v={scoped.length.toLocaleString()} k={`cheer ${noun.toLowerCase()}s in the TAM`} />
        <Score
          v={`${partners.toLocaleString()}`}
          k="partners (contract + exclusive jewelry)"
          sub={scoped.length ? `${Math.round((partners / scoped.length) * 100)}% of the TAM` : ''}
        />
        <Score v={titleTotal.toLocaleString()} k="championship relationships" sub="titles won by these gyms’ teams" />
        {rollup && (
          <Score
            v={rollup.matchedTotal.toLocaleString()}
            k="cheer titles linked to a gym"
            sub={`${rollup.unmatched.length.toLocaleString()} of ${rollup.cheerTotal.toLocaleString()} not yet linkable`}
            gap={rollup.unmatched.length > 0}
          />
        )}
      </div>

      {/* ⚠️ THE COMPETITOR'S BOOK — GRADED, because the flat version was wrong.
          Three numbers that mean different things and must never be summed: a
          gym Herff Jones is ACTIVELY selling is a live loss; a gym whose
          storefront is empty or years cold is the opposite of a loss (we may
          already have won it); and their gyms we hold no record for are
          expansion that cannot appear on this board until the company exists. */}
      <div className={styles.scoreRow}>
        <Score
          v={hj.theirs.toLocaleString()}
          k={`gyms with ${HERFF_JONES} as the incumbent`}
          sub={hj.captured ? `their public storefront, captured ${hj.captured}` : ''}
        />
        <Score
          v={hj.confirmed.toLocaleString()}
          k="ours — and actively theirs"
          sub="HJ added product in the last year — a live loss"
          gap={hj.confirmed > 0}
        />
        <Score
          v={hj.lapsed.toLocaleString()}
          k="ours — HJ gone cold"
          sub="empty or 2+ years stale on their side — likely already won"
        />
        <Score
          v={hj.notHeld.toLocaleString()}
          k="their gyms we have NO record for"
          sub="not on this board — nothing to work until the company exists"
          gap={hj.notHeld > 0}
        />
        {hj.possible > 0 && (
          <Score
            v={hj.possible.toLocaleString()}
            k="possible name match"
            sub="close, not exact — confirm before calling it an incumbent"
            gap
          />
        )}
      </div>

      <div className={styles.controls}>
        <label className={styles.control}>
          <span>Sort gyms by</span>
          <select className="input" value={sort} onChange={(e) => setSort(e.target.value)}>
            {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </label>
        <label className={styles.control}>
          <span>Championship footprint</span>
          <select className="input" value={footprint} onChange={(e) => setFootprint(e.target.value)}>
            <option value="all">All gyms</option>
            <option value="titled">Has championship relationships</option>
            <option value="none">No titles matched yet</option>
          </select>
        </label>
        <span className={styles.controlNote}>
          Showing {scoped.length.toLocaleString()} of {items.length.toLocaleString()} gyms
        </span>
      </div>

      <div className={styles.pipeline}>
        {PARTNERSHIP_STAGES.map((s) => (
          <Column
            key={s.key}
            stage={s}
            count={stages.find((x) => x.key === s.key)?.count || 0}
            gyms={byStage[s.key]}
            onOpen={onOpen}
            onOpenTitle={onOpenTitle}
            rowAction={rowAction}
          />
        ))}
      </div>

      {/* THE UNMATCHED TITLES — said out loud, never dropped. A cheer title
          whose crowned team doesn't resolve to a gym in the TAM is either a gym
          we don't have or a name we can't link yet, and both are work. */}
      {rollup && rollup.unmatched.length > 0 && <Unmatched rollup={rollup} onOpenTitle={onOpenTitle} />}
    </div>
  )
}

function Score({ v, k, sub = '', gap = false }) {
  return (
    <div className={`${styles.score} ${gap ? styles.scoreGap : ''}`}>
      <div className={styles.scoreV}>{v}</div>
      <div className={styles.scoreK}>{k}</div>
      {sub && <div className={styles.scoreSub}>{sub}</div>}
    </div>
  )
}

function Column({ stage, count, gyms, onOpen, onOpenTitle, rowAction }) {
  const [limit, setLimit] = useState(PAGE)
  const shown = gyms.slice(0, limit)
  return (
    <section className={styles.col} style={{ '--tone': stage.color }}>
      <header className={styles.colHead}>
        <span className={styles.colStep}>{stage.step}</span>
        <span className={styles.colTitle}>{stage.label}</span>
        <span className={styles.colCount}>{count.toLocaleString()}</span>
      </header>
      <p className={styles.colHint}>{stage.hint}</p>
      <div className={styles.colBody}>
        {shown.map((c) => (
          <GymCard key={c.id} gym={c} onOpen={onOpen} onOpenTitle={onOpenTitle} rowAction={rowAction} />
        ))}
        {!gyms.length && <div className={styles.colEmpty}>No gyms at this stage.</div>}
        {gyms.length > shown.length && (
          <button type="button" className={styles.more} onClick={() => setLimit(limit + PAGE * 5)}>
            Show {Math.min(PAGE * 5, gyms.length - shown.length).toLocaleString()} more
            <span className={styles.moreOf}> · {shown.length.toLocaleString()} of {gyms.length.toLocaleString()}</span>
          </button>
        )}
      </div>
    </section>
  )
}

/**
 * ONE GYM — the partnership state, the championship footprint, the teams
 * underneath it, and the one next step. Everything a rep needs to act, without
 * opening anything.
 */
function GymCard({ gym, onOpen, onOpenTitle, rowAction }) {
  const [openTeams, setOpenTeams] = useState(false)
  const meta = partnershipMeta(gym)
  const owner = ownerName(gym)
  const titles = gym.titleCount || 0
  const teams = gym.teams || []

  return (
    <article className={styles.card} style={{ '--tone': meta.color }}>
      <div className={styles.cardTop}>
        <button type="button" className={styles.cardName} onClick={() => onOpen?.(gym)} title="Open the full gym record">
          {gym.name}<span className={styles.openHint}> ›</span>
        </button>
        {gym.state && gym.state !== 'NATIONAL'
          ? <span className={shared.badgeDim}>{gym.state}</span>
          : <span className={shared.badgeGap}>no state</span>}
        <IncumbentBadge gym={gym} />
      </div>

      {/* THE PARTNERSHIP — both halves, always both shown, so a signed contract
          with no jewelry program can never read as a partnership. */}
      <div className={styles.halves}>
        <span className={`${styles.half} ${meta.signed ? styles.halfOn : styles.halfOff}`}>
          {meta.signed ? '✓' : '○'} Contract
          {meta.signedAt && <span className={styles.halfWhen}>{formatDate(meta.signedAt)}</span>}
        </span>
        <span className={`${styles.half} ${meta.jewelry.live ? styles.halfOn : styles.halfOff}`}>
          {meta.jewelry.live ? '✓' : '○'} Exclusive jewelry
          {meta.jewelry.present && !meta.jewelry.live && (
            <span className={styles.halfWhen}>{meta.jewelry.label}</span>
          )}
        </span>
      </div>

      {/* THE CHAMPIONSHIP FOOTPRINT — the reason to go after this gym. */}
      <button
        type="button"
        className={`${styles.footprint} ${titles ? styles.footprintOn : styles.footprintNone}`}
        onClick={() => setOpenTeams((v) => !v)}
        disabled={!titles}
        title={titles ? 'Show the teams and the titles they won' : 'No cheer title has been matched to this gym yet'}
      >
        {titles
          ? <>♛ {titles} championship{titles === 1 ? '' : 's'} · {gym.teamCount} team{gym.teamCount === 1 ? '' : 's'}
            {gym.lastTitleAt && <span className={styles.footLast}>last {formatDate(gym.lastTitleAt)}</span>}
            <span className={styles.footToggle}>{openTeams ? 'hide' : 'teams'}</span></>
          : <>no championship titles matched yet</>}
      </button>

      {/* THE TEAM LAYER — gym → team → championship, made real. */}
      {openTeams && titles > 0 && (
        <ul className={styles.teams}>
          {teams.map((t) => (
            <li key={t.name || '__gym__'} className={styles.team}>
              <span className={styles.teamName}>
                {t.name || <em className={styles.teamGymLevel}>gym-level title</em>}
                <span className={styles.teamCount}>{t.titleCount}</span>
              </span>
              <span className={styles.teamTitles}>
                {t.titles.map((tt) => (
                  <button
                    key={tt.id}
                    type="button"
                    className={styles.titleChip}
                    style={{ borderColor: sportMeta(tt.sport).color + '66' }}
                    onClick={() => onOpenTitle?.(tt)}
                    title={`${tt.name}${tt.effDate ? ` · ${formatDate(tt.effDate)}` : ''} — open the championship record`}
                  >
                    {tt.name}
                  </button>
                ))}
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className={styles.cardMeta}>
        {owner ? <span className={styles.owner}>{owner}</span> : <span className={shared.gapFlag}>UNASSIGNED</span>}
        {gym.gymTeamCount != null && <span className={styles.metaDim}>{gym.gymTeamCount} teams on record</span>}
        {gym.product && <span className={styles.metaDim}>{gym.product}</span>}
        {gym.sourceUrl && <SubmitLink record={gym} />}
      </div>

      {/* THE ONE MOVE. Paint-by-numbers: the gym's own state picks it. */}
      {rowAction && <NextStep record={gym} action={rowAction} onOpen={onOpen} />}
    </article>
  )
}

/**
 * THE HONEST GAP. Cheer titles whose crowned team doesn't resolve to any gym in
 * the TAM. Shown, counted and listed — because "we can't link this yet" is a
 * data job with a name, and hiding it would make every count above look complete
 * when it isn't.
 */
function Unmatched({ rollup, onOpenTitle }) {
  const [open, setOpen] = useState(false)
  const shown = rollup.unmatched.slice(0, 60)
  return (
    <div className={styles.unmatched}>
      <button type="button" className={styles.unmatchedHead} onClick={() => setOpen((v) => !v)}>
        <strong>{rollup.unmatched.length.toLocaleString()} cheer titles are not linked to a gym.</strong>
        {' '}Their crowned team doesn’t resolve to any gym in this TAM — either the gym isn’t in HubSpot yet,
        or the team name can’t be matched to one. {open ? 'Hide' : 'Show'} them.
      </button>
      {open && (
        <ul className={styles.unmatchedList}>
          {shown.map((u, i) => (
            <li key={`${u.title.id}-${i}`}>
              <button type="button" className={styles.unmatchedItem} onClick={() => onOpenTitle?.(u.title)}>
                <span className={styles.unmatchedChamp}>{u.champion || <em>no champion recorded</em>}</span>
                <span className={styles.unmatchedTitle}>{u.title.name}</span>
              </button>
            </li>
          ))}
          {rollup.unmatched.length > shown.length && (
            <li className={styles.unmatchedMore}>
              …and {(rollup.unmatched.length - shown.length).toLocaleString()} more.
            </li>
          )}
        </ul>
      )}
    </div>
  )
}

/**
 * WHO SUPPLIES THIS GYM TODAY — graded, dated, and never overstated.
 *
 * ⚠️ THIS BADGE USED TO LIE, quietly, on more than half the gyms it appeared on.
 * It rendered "Herff Jones ↗" for any name on their storefront. Brian: "They
 * each aren't HJ incumbents because some of them are from 2022 and we've picked
 * up a lot of new accounts." He was right — 77 of those collections hold ZERO
 * products and 57 more are over two years cold.
 *
 * The damage was specific: a rep opening a call on a gym SR may already have
 * won, telling them we know they buy from Herff Jones.
 *
 * Four states now, and only one of them names an incumbent:
 *   ACTIVE   → "Herff Jones · $475" — real, current, with the price they pay.
 *   COOLING  → "HJ cooling" — on the list, nothing new in 1–2 years.
 *   LAPSED   → "HJ lapsed 2023" — ⚠️ rendered as an OPPORTUNITY, not a warning.
 *   EMPTY    → "HJ page, no products" — a parked page, evidence of nothing.
 */
function IncumbentBadge({ gym }) {
  const inc = incumbent(gym)
  if (inc.confidence === 'unknown') return null

  if (inc.confidence === 'possible') {
    return (
      <span className={shared.badgeGap} title={`${inc.reading}\n\nClose to: ${inc.candidates.join(', ')}`}>
        {HERFF_JONES}?
      </span>
    )
  }

  /* The receipt. A rep challenged on "how do you know" opens their storefront
     for this gym — which is also why the claim is worth making out loud. */
  const link = (label, cls) => (
    <a
      className={cls}
      href={inc.evidenceUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      title={`${inc.reading}\n\nListed by Herff Jones as "${inc.matchedName}". Opens their storefront for this gym.`}
    >
      {label}
    </a>
  )

  if (inc.confidence === 'confirmed') {
    /* ⚠️ THE PRICE IS THE POINT, per Brian: "High priced rings most likely means
       open to legacy." A gym already paying $475 a ring is buying on quality,
       which is the argument SR wins on. Shown only when it is a real per-ring
       price — a team-package line item is not what one athlete pays. */
    const price = inc.medPrice != null && inc.medPrice <= PACKAGE_PRICE ? ` · $${Math.round(inc.medPrice)}` : ''
    return link(`${HERFF_JONES}${price} ↗`, shared.badgeGap)
  }

  if (inc.confidence === 'cooling') return link('HJ cooling ↗', shared.badgeDim)

  /* ⚠️ LAPSED AND EMPTY ARE GOOD NEWS and are drawn in the neutral style, not
     the gap style. Drawing a competitor who has stopped selling to this gym as
     a red flag is exactly backwards. */
  if (inc.confidence === 'lapsed') {
    return link(`HJ lapsed ${String(inc.lastPublished || '').slice(0, 4)} ↗`, shared.badgeDim)
  }
  return link('HJ page, no products ↗', shared.badgeDim)
}
